import * as THREE from 'three';

/**
 * Every texture in the game is drawn on a canvas at runtime.
 *
 * Nothing is downloaded and nothing is committed as an image: the game has to work with no
 * network at all, and a 4 KB draw routine beats a 40 KB PNG that has to be decoded before
 * the first frame anyway.
 *
 * The building facades used to be drawn here too, as six canvas tiles; they are drawn in the
 * shader now — see `facade.ts`.
 */

/**
 * Metres of facade per window bay, and per storey: the defaults wall UVs are built in. A
 * building picks its own bay width around the first (`BAY_RANGE` in `worldMesh.ts`).
 */
export const BAY_METRES = 3.6;
export const FLOOR_METRES = 3.2;

/** Metres covered by one repeat of the lane-marking texture. */
export const MARKING_PERIOD_METRES = 14;

/**
 * Lane markings for the wider roads, drawn across the full carriageway.
 *
 * `u` runs across the road (0 at one kerb, 1 at the other) and `v` along it, one repeat per
 * `MARKING_PERIOD_METRES`, so the dash rhythm is a fixed distance on the ground rather than
 * a fixed number of dashes per street. Everything except the paint is transparent — this is
 * a decal laid over the tarmac, so it works on any road colour.
 */
export function makeLaneMarkingTexture(): THREE.Texture {
  const w = 64;
  const h = 256;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);

  // Not white, and not fully opaque. Lit paint at full brightness clips to a solid white
  // bar under the front bumper, which is the one place the player is definitely looking.
  const paint = 'rgba(226, 223, 211, 0.72)';
  ctx.fillStyle = paint;

  // Centre line, solid, straddling u = 0.5.
  ctx.fillRect(w / 2 - 1.5, 0, 3, h);

  // Lane dashes either side. 4 m of paint in every 14 m, which at 60 km/h is about the
  // rhythm you expect out of a windscreen.
  const dash = Math.round((4 / MARKING_PERIOD_METRES) * h);
  for (const u of [0.25, 0.75]) {
    ctx.fillRect(Math.round(w * u) - 1, Math.round(h * 0.5 - dash / 2), 2, dash);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  // u is exactly 0..1 across the ribbon, so clamping avoids the far kerb bleeding paint.
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

/** Stripes in one crossing, counting the gaps. Odd, so it starts and ends on paint. */
const ZEBRA_STRIPES = 9;

/**
 * A zebra crossing, as its own decal.
 *
 * It cannot share the lane-marking texture, and the reason is the UV convention rather than
 * space on the canvas. Lane markings run `v` *along* the street, repeating every
 * `MARKING_PERIOD_METRES`, so anything drawn into that tile appears again every 14 m of road.
 * A crossing happens once, at a junction mouth. Its own texture and its own quad is one extra
 * draw call for the whole city and no compromise on either.
 *
 * `u` runs across the carriageway and `v` along it, both clamped: a crossing is a fixed patch
 * of paint, not a repeat.
 */
export function makeCrossingTexture(): THREE.Texture {
  const w = 128;
  const h = 64;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, w, h);

  // The same paint as the lane markings, and for the same reason: lit white at full
  // brightness clips to a solid bar right under the front bumper.
  ctx.fillStyle = 'rgba(226, 223, 211, 0.72)';
  const band = w / ZEBRA_STRIPES;
  for (let i = 0; i < ZEBRA_STRIPES; i += 2) {
    // Inset along v so the stripes stop short of the kerb, which is where the paint stops on
    // a real crossing — running them to the very edge reads as a solid white slab.
    ctx.fillRect(Math.round(i * band), Math.round(h * 0.06), Math.ceil(band), Math.round(h * 0.88));
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.anisotropy = 8;
  return texture;
}
